import React, { useCallback, useContext, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { parseAnonymousLearningItem } from '../models/AnonymousLearningItem'

import { dummyGroup, parseGroup } from '../models/Group'
import { dummyLearningItem, LearningItem } from '../models/LearningItem'
import { dummyProvider, parseProvider } from '../models/Provider'
import { dummyTrainer, parseTrainer } from '../models/Trainer'
import { dummyUser, parseUser } from '../models/User'
import parseModel from '../models/parseModel'
import * as api from './api'
import useUserLearningItems from './queries/useUserLearningItems'
import { useSession } from './session'
import { usersSlice, groupsSlice, learningItemsSlice, providersSlice, trainersSlice } from './store'

type BaseEntity = { uuid: string }

/**********************************************************************
 * EntityConfiguration
 **********************************************************************/
class EntityConfiguration<T extends BaseEntity> {
  endpoint: string
  slice: any
  parse: ReturnType<typeof parseModel<T>>
  createDummy: (uuid: string, error?: any) => T

  constructor(init: {
    endpoint: string
    slice: any
    parse: EntityConfiguration<T>['parse']
    createDummy: EntityConfiguration<T>['createDummy']
  }) {
    this.endpoint = init.endpoint
    this.slice = init.slice
    this.parse = init.parse
    this.createDummy = init.createDummy
  }

  async fetch(uuids: string[], dispatch: any) {
    try {
      const res = await api.post(this.endpoint, uuids)

      const uuidsSet = new Set(uuids)
      const entities = []
      for (const resItem of res) {
        const entity = this.parse(resItem)
        uuidsSet.delete(entity.uuid)
        entities.push(entity)
      }
      for (const missingUuid of uuidsSet) {
        entities.push(this.createDummy(missingUuid, new Error('Not found')))
      }
      dispatch(this.slice.actions.add(entities))
      return entities
    } catch (err) {
      const entities = uuids.map((uuid) => this.createDummy(uuid, err))
      dispatch(this.slice.actions.add(entities))
      return entities
    }
  }
}

type EntitiesConfiguration<T extends { [k: string]: BaseEntity }> = {
  [k in keyof T]: EntityConfiguration<T[k]>
}

/**********************************************************************
 * Queue
 **********************************************************************/
type Queue<T extends object> = { triggered: boolean; types: { [k in keyof T]: Set<string> } }

function createQueue<T extends { [k: string]: BaseEntity }>(config: EntitiesConfiguration<T>) {
  return {
    triggered: false,
    types: Object.fromEntries(Object.keys(config).map((k) => [k, new Set<string>()])),
  } as Queue<T>
}

/**********************************************************************
 * createEntitiesContext
 **********************************************************************/
function createEntitiesContext<T extends { [k: string]: BaseEntity }>(config: EntitiesConfiguration<T>) {
  // context
  type EntitiesContext = {
    requestEntities: (type: keyof T, uuids: string[]) => void
  }

  const context = React.createContext<EntitiesContext>({
    requestEntities: () => {
      throw new Error('EntitiesProvider missing')
    },
  })

  // provider
  const provider = React.memo(function EntitiesProvider({ children }: React.PropsWithChildren) {
    const dispatch = useDispatch()

    const queue = useMemo(() => createQueue(config), [])

    const processQueue = useCallback(() => {
      // console.log('process queue')
      for (const [key, uuidSet] of Object.entries(queue.types) as [keyof T, Set<string>][]) {
        if (uuidSet.size) {
          const uuids = Array.from(uuidSet)
          uuidSet.clear()
          dispatch(config[key].slice.actions.requested(uuids))
          config[key].fetch(uuids, dispatch)
        }
      }
      queue.triggered = false
    }, [queue, dispatch])

    const requestEntities = useCallback(
      (type: keyof T, uuids: string[]) => {
        const prevSize = queue.types[type].size
        for (const uuid of uuids) {
          queue.types[type].add(uuid)
        }

        if (!queue.triggered && prevSize !== queue.types[type].size) {
          queue.triggered = true
          queueMicrotask(() => processQueue())
        }
      },
      [queue, processQueue]
    )

    const ctx = useMemo(() => ({ requestEntities }), [requestEntities])

    return <context.Provider value={ctx}>{children}</context.Provider>
  })

  // hooks
  const useEntitiesContext = () => useContext(context)

  const useEntities = <KEY extends keyof T>(type: KEY, uuids: (string | null | undefined)[]) => {
    const { requestEntities } = useEntitiesContext()

    const requests: string[] = []
    const entities = useSelector((state: any /* FIXME RootState */) => {
      const entities = []
      for (const uuid of uuids) {
        if (!uuid) {
          entities.push(config[type].createDummy('empty'))
          continue
        }
        if (uuid.startsWith('dummy-')) {
          entities.push(config[type].createDummy(uuid))
          continue
        }
        const entity = state[type].byId[uuid]
        const { requested, stale } = state[type].metaById[uuid] || {}
        if ((!entity || stale) && !requested) {
          requests.push(uuid)
        }
        entities.push(entity || config[type].createDummy(uuid))
      }
      return entities
    }, shallowEqual)

    if (requests.length > 0) {
      requestEntities(type, requests)
    }

    return entities as Awaited<ReturnType<(typeof config)[KEY]['fetch']>>
  }

  const useEntity = <KEY extends keyof T>(type: KEY, uuid: string | null | undefined) => {
    return useEntities(type, [uuid])[0]
  }

  return { context, provider, useEntitiesContext, useEntities, useEntity }
}

/**********************************************************************
 * concrete entities
 **********************************************************************/
const entitiesConfiguration = {
  users: new EntityConfiguration({
    endpoint: 'user/by-ids',
    createDummy: dummyUser,
    parse: parseUser,
    slice: usersSlice,
  }),
  groups: new EntityConfiguration({
    endpoint: 'group/by-ids',
    createDummy: dummyGroup,
    parse: parseGroup,
    slice: groupsSlice,
  }),
  learningItems: new EntityConfiguration({
    endpoint: 'course/by-ids',
    createDummy: dummyLearningItem,
    parse: parseAnonymousLearningItem,
    slice: learningItemsSlice,
  }),
  providers: new EntityConfiguration({
    endpoint: 'provider/by-ids',
    createDummy: dummyProvider,
    parse: parseProvider,
    slice: providersSlice,
  }),
  trainers: new EntityConfiguration({
    endpoint: 'trainer/by-ids',
    createDummy: dummyTrainer,
    parse: parseTrainer,
    slice: trainersSlice,
  }),
}

const { provider, useEntities, useEntity } = createEntitiesContext(entitiesConfiguration)

export const EntityContextProvider = provider

export const useUsers = (uuids: (string | undefined | null)[]) => ({ users: useEntities('users', uuids) })
export const useUser = (uuid: string | undefined | null) => ({ user: useEntity('users', uuid) })
export const useGroups = (uuids: (string | undefined | null)[]) => ({ groups: useEntities('groups', uuids) })
export const useGroup = (uuid: string | undefined | null) => ({ group: useEntity('groups', uuid) })
export const useLearningItems = (uuids: (string | undefined | null)[]) => {
  const { user } = useSession()
  const { userLearningItems, isPlaceholderData } = useUserLearningItems(user?.uuid)
  const anonymousLearningItems = useEntities('learningItems', uuids)
  const learningItems = useMemo(
    () =>
      isPlaceholderData
        ? uuids.map((uuid) => dummyLearningItem(uuid || 'empty'))
        : uuids.map((uuid, idx) => userLearningItems.find((item) => item.uuid === uuid) || anonymousLearningItems[idx]),
    [uuids, anonymousLearningItems, userLearningItems, isPlaceholderData]
  ) as LearningItem[]

  return { learningItems }
}

export const useLearningItem = (uuid: string | undefined | null) => {
  const { user } = useSession()
  const { userLearningItems, isPlaceholderData } = useUserLearningItems(user?.uuid)
  const anonymousLearningItem = useEntity('learningItems', uuid)
  const learningItem = useMemo(
    () =>
      isPlaceholderData
        ? dummyLearningItem(uuid || 'empty')
        : userLearningItems.find((item) => item.uuid === uuid) || anonymousLearningItem,
    [uuid, anonymousLearningItem, userLearningItems, isPlaceholderData]
  ) as LearningItem

  return { learningItem }
}

export const useLearningItemAsEntity = (uuid: string | undefined | null) => {
  const learningItem = useEntity('learningItems', uuid)

  return { learningItem }
}

export const useProviders = (uuids: (string | undefined | null)[]) => ({ providers: useEntities('providers', uuids) })
export const useProvider = (uuid: string | undefined | null) => ({ provider: useEntity('providers', uuid) })

export const useTrainers = (uuids: (string | undefined | null)[]) => ({ trainers: useEntities('trainers', uuids) })
export const useTrainer = (uuid: string | undefined | null) => ({ trainer: useEntity('trainers', uuid) })
